import {
  Grid,
  GridColumn as Column,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuFilter,
} from "@progress/kendo-react-grid";
import * as React from "react";
import { process as processData } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import ErrorBanner from "../../components/ErrorBanner";

import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";

import {
  useQueryGetDataSyncHistory,
  useMutateDataSync,
} from "../../data/services/workflowService";

const pagerSetting = {
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: [10, 20, 50, 100, 200, 500],
  previousNext: true,
};

const QUEUED_BLOCK_WINDOW_MS = 5 * 60 * 1000;
const RUNNING_BLOCK_WINDOW_MS = 60 * 60 * 1000;

const isRecentInProgressSync = (historyItem) => {
  if (!(historyItem?.syncStatus === "Q" || historyItem?.syncStatus === "R")) {
    return false;
  }

  if (historyItem?.endDate) {
    return false;
  }

  const startedAt =
    historyItem?.startDate || historyItem?.dateCreated || historyItem?.dateUpdated;
  const startTime = Date.parse(startedAt);
  if (Number.isNaN(startTime)) {
    return historyItem?.syncStatus === "R";
  }

  const blockWindow = historyItem?.syncStatus === "Q"
    ? QUEUED_BLOCK_WINDOW_MS
    : RUNNING_BLOCK_WINDOW_MS;

  return startTime + blockWindow > Date.now();
};

const BrokerUserSyncGrid = () => {
  const oePeriod = React.useContext(OEContext);
  const { loggedInUser } = React.useContext(UserContext);

  const [gridDataSrc, setGridDataSrc] = React.useState([]);
  const [gridDataMasterSrc, setGridDataMasterSrc] = React.useState([]);
  const [expandedErrors, setExpandedErrors] = React.useState({});
  const [createMissingBrokers, setCreateMissingBrokers] = React.useState(false);
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
  });

  const {
    isLoading: historyLoading,
    data: historyList,
    refetch,
  } = useQueryGetDataSyncHistory("BrokerUsers", oePeriod?.oePeriodId);
  const useMutateDataSyncHandler = useMutateDataSync();
  const hasSyncInProgress = gridDataMasterSrc.some(isRecentInProgressSync);

  const isProductionEnvironment = process.env.REACT_APP_IS_PRODUCTION_ENVIRONMENT === "true";

  React.useEffect(() => {
    if (!historyLoading && historyList && historyList.data) {
      setGridDataMasterSrc(historyList.data);
      setGridDataSrc(processData(historyList.data.slice(0), gridDataState));
    }
  }, [historyLoading, historyList, gridDataState]);

  if (historyLoading) {
    return "Loading ...";
  }

  const handleBrokerSync = async () => {
    const data = {
      oePeriodId: oePeriod.oePeriodId,
      dataType: "BrokerUsers",
      createMissingBrokers: createMissingBrokers,
    };

    await useMutateDataSyncHandler.mutateAsync(data);
    refetch();
  };

  const dataStateChange = (event) => {
    setGridDataSrc(processData(gridDataMasterSrc.slice(0), event.dataState));
    setGridDataState(event.dataState);
  };

  const columnMenuHeader = (field) => {
    const active = GridColumnMenuFilter.active(field, gridDataState.filter);
    return active ? "active" : "";
  };

  const ColumnCheckboxMenu = (props) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={gridDataMasterSrc}
          expanded={true}
        />
      </div>
    );
  };

  const DateCell = (props) => {
    return (
      <td>
        {props.dataItem[props.field] &&
          new Date(props.dataItem[props.field] + "Z").toLocaleString("en-US")}
      </td>
    );
  };

  const toggleErrorExpansion = (dataSyncHistoryId) => {
    setExpandedErrors((current) => ({
      ...current,
      [dataSyncHistoryId]: !current[dataSyncHistoryId],
    }));
  };

  const ErrorMessageCell = (props) => {
    const errorMessage = props.dataItem[props.field] || "";
    const dataSyncHistoryId = props.dataItem.dataSyncHistoryId;
    const isExpandable = errorMessage.length > 120;
    const isExpanded = Boolean(expandedErrors[dataSyncHistoryId]);

    return (
      <td>
        <div
          onClick={isExpandable ? () => toggleErrorExpansion(dataSyncHistoryId) : undefined}
          style={{
            cursor: isExpandable ? "pointer" : "default",
            maxWidth: 500,
            overflow: "hidden",
            textOverflow: isExpanded ? "clip" : "ellipsis",
            whiteSpace: isExpanded ? "normal" : "nowrap",
            wordBreak: "break-word",
          }}
          title={isExpandable && !isExpanded ? "Click to expand" : errorMessage}
        >
          {errorMessage}
          {isExpandable && (
            <span style={{ color: "#192853", fontWeight: 600, marginLeft: 8 }}>
              {isExpanded ? "Show less" : "Show more"}
            </span>
          )}
        </div>
      </td>
    );
  };

  return (
    <div>
      <div
        style={{
          justifyContent: "space-between",
          display: "flex",
          paddingBottom: 10,
        }}
      >
        <div>
          <h2 style={{ margin: "10px" }}>Broker Sync History</h2>
        </div>
        <div style={{ alignSelf: "center", textAlign: "right" }}>
          {loggedInUser && loggedInUser.hasDataSyncPriv && (
            <>
              <Button
                onClick={handleBrokerSync}
                themeColor={"success"}
                disabled={
                  hasSyncInProgress || Boolean(useMutateDataSyncHandler.isLoading)
                }
              >
                Sync Broker Users
              </Button>
              {isProductionEnvironment && (
                <div style={{ marginTop: 10 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Switch
                      checked={createMissingBrokers}
                      onChange={(e) => setCreateMissingBrokers(Boolean(e.target.value))}
                      disabled={hasSyncInProgress || Boolean(useMutateDataSyncHandler.isLoading)}
                    />
                    <span>Create brokers</span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ErrorBanner handler={useMutateDataSyncHandler} />
      <Grid
        sortable={true}
        pageable={pagerSetting}
        pageSize={20}
        data={gridDataSrc}
        {...gridDataState}
        onDataStateChange={dataStateChange}
      >
        <Column field="startDate" filter="text" title="Start Date" cell={DateCell} />
        <Column field="endDate" filter="text" title="End Date" cell={DateCell} />
        <Column
          field="syncStatusDesc"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("syncStatusDesc")}
          title="Sync Status"
        />
        <Column field="errorMessage" filter="text" title="Error Message" cell={ErrorMessageCell} />
      </Grid>
    </div>
  );
};

export default BrokerUserSyncGrid;